import { FC, useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { FeeConfigGroupViewModel } from '../../models/fees';

const UNGROUPED_VALUE = -1;

interface MoveToGroupOption {
  label: string;
  value: number;
  disabled: boolean;
}

export interface MoveToGroupDialogProps {
  visible: boolean;
  groups: FeeConfigGroupViewModel[];
  configCount: number;
  /**
   * Dropdown values (group id, or UNGROUPED_VALUE for ungrouped) the moved configs already belong to.
   * A target shared by every moved config is disabled, since moving there would be a no-op.
   */
  currentGroupValues: number[];
  onClose: () => void;
  onMove: (targetGroupId: number | null) => Promise<void> | void;
}

// Builds indented labels ("— Branch 110") so the group hierarchy is readable in a flat dropdown.
const buildGroupOptions = (groups: FeeConfigGroupViewModel[]): { label: string; value: number }[] => {
  const childrenByParent = new Map<number | null, FeeConfigGroupViewModel[]>();
  for (const group of groups) {
    // The API omits null fields (WhenWritingNull), so a root group's parentFeeConfigGroupId
    // arrives as undefined; normalize to null so it keys under the root bucket.
    const key = group.parentFeeConfigGroupId ?? null;
    const siblings = childrenByParent.get(key) ?? [];
    siblings.push(group);
    childrenByParent.set(key, siblings);
  }
  for (const siblings of childrenByParent.values()) {
    siblings.sort((first, second) => first.sortOrder - second.sortOrder || first.name.localeCompare(second.name));
  }

  const options: { label: string; value: number }[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const group of childrenByParent.get(parentId) ?? []) {
      options.push({ label: `${'\u00a0\u00a0'.repeat(depth)}${depth > 0 ? '\u2514 ' : ''}${group.name}`, value: group.feeConfigGroupId });
      walk(group.feeConfigGroupId, depth + 1);
    }
  };
  walk(null, 0);
  return options;
};

export const MoveToGroupDialog: FC<MoveToGroupDialogProps> = ({ visible, groups, configCount, currentGroupValues, onClose, onMove }) => {
  // A target is a no-op only when every moved config already lives there.
  const disabledValue = currentGroupValues.length === 1 ? currentGroupValues[0] : null;

  const options = useMemo<MoveToGroupOption[]>(
    () =>
      [{ label: 'Ungrouped (always evaluated)', value: UNGROUPED_VALUE }, ...buildGroupOptions(groups)].map((option) => ({
        ...option,
        disabled: option.value === disabledValue,
      })),
    [groups, disabledValue]
  );

  const firstSelectableValue = useMemo(() => options.find((option) => !option.disabled)?.value ?? null, [options]);

  // Initialized once per mount; the parent remounts this dialog via `key` each time it opens, so a
  // background refetch that reorders options cannot clobber the user's in-progress selection.
  const [selectedValue, setSelectedValue] = useState<number | null>(firstSelectableValue);
  const [isSaving, setIsSaving] = useState(false);

  const handleMove = async () => {
    if (selectedValue === null) return;
    setIsSaving(true);
    try {
      await onMove(selectedValue === UNGROUPED_VALUE ? null : selectedValue);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header="Move to group"
      modal
      style={{ width: '480px', maxWidth: '90vw' }}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label="Cancel" className="p-button-text" onClick={onClose} disabled={isSaving} />
          <Button label="Move" icon="pi pi-check" onClick={handleMove} loading={isSaving} disabled={selectedValue === null} />
        </div>
      }
    >
      <div className="flex flex-column gap-2">
        <span className="text-gray-600">
          Move {configCount} {configCount === 1 ? 'configuration' : 'configurations'} to:
        </span>
        <Dropdown
          value={selectedValue}
          options={options}
          optionLabel="label"
          optionValue="value"
          optionDisabled="disabled"
          onChange={(event) => setSelectedValue(event.value)}
          className="w-full border-1 border-solid border-gray-300"
        />
      </div>
    </Dialog>
  );
};
